import React from 'react';

import { ButtonProps, Grid, TextFieldProps } from '@ha/dsl';

import {
  Calendar,
  CalendarFooter,
  CalendarInfo,
  DayPickerProps,
  RangeModifier,
} from 'ha/ui/next/DatePicker';

import DateRangePickerInputsContainer from './DateRangePickerInputs';
import {
  DatePickerInputsProps,
  DateRangePickerProps,
  FOCUSED_FIELD,
  OverlayProps,
} from '../types';

interface Props {
  AdditionalInfo?: React.ReactNode;
  autoClose: boolean;
  buttonProps: ButtonProps;
  noEndDate: boolean;
  noEndDateLabel: string;
  CaptionSubtitle?: DateRangePickerProps['CaptionSubtitle'];
  DatePickerInputs: React.ComponentType<DatePickerInputsProps>;
  datePickerProps: DayPickerProps;
  focused: FOCUSED_FIELD | null;
  from?: Date;
  fromEmptyValueLabel: string;
  to?: Date;
  onDatesClear?: () => void;
  onNoEndDate: () => void;
  onOverlayExit: () => void;
  onOverlayOpen: (field: FOCUSED_FIELD) => void;
  overlayCloseButton: string;
  clearDatesButton?: string;
  fromInputProps: TextFieldProps;
  minimumPeriodHint: string;
  moveInWindows: RangeModifier[];
  moveInWindowsHint: string;
  moveInWindowsTooltip: string;
  toInputProps: TextFieldProps;
  toEmptyValueLabel: string;
  Overlay: React.FC<OverlayProps>;
  header?: React.ReactNode;
}

const OverlayWrapper: React.FC<Props> = props => {
  const {
    AdditionalInfo,
    autoClose,
    buttonProps,
    noEndDate,
    noEndDateLabel,
    CaptionSubtitle,
    DatePickerInputs,
    focused,
    from,
    to,
    onDatesClear,
    onNoEndDate,
    onOverlayExit,
    onOverlayOpen,
    Overlay,
    overlayCloseButton,
    clearDatesButton,
    fromInputProps,
    toInputProps,
    fromEmptyValueLabel,
    toEmptyValueLabel,
    datePickerProps,
    minimumPeriodHint,
    moveInWindows,
    moveInWindowsHint,
    moveInWindowsTooltip,
    header,
  } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (open && !focused && autoClose) {
      setOpen(false);
    }
  }, [focused, open]);

  const onOverlayOpenHandler = React.useCallback(
    (field: FOCUSED_FIELD) => {
      setOpen(true);
      onOverlayOpen(field);
    },
    [onOverlayOpen],
  );
  const onOverlayCloseHandler = React.useCallback(() => {
    setOpen(false);
  }, []);
  const onNoEndDateHandler = React.useCallback(() => {
    onNoEndDate();
    setOpen(false);
  }, [onNoEndDate]);

  return (
    <Grid container justify="center" ref={ref}>
      <DateRangePickerInputsContainer
        focused={focused}
        from={from}
        fromEmptyValueLabel={fromEmptyValueLabel}
        fromInputProps={fromInputProps}
        onFieldFocusChange={onOverlayOpenHandler}
        to={to}
        toEmptyValueLabel={toEmptyValueLabel}
        toInputProps={toInputProps}
      >
        {inputsProps => <DatePickerInputs {...inputsProps} />}
      </DateRangePickerInputsContainer>
      <Overlay
        containerRef={ref}
        focused={focused}
        from={from}
        onFieldFocusChange={onOverlayOpen}
        onOverlayExit={onOverlayExit}
        onOverlayClose={onOverlayCloseHandler}
        open={open}
        OverlayTitle={header}
        OverlayActions={
          <CalendarFooter
            buttonProps={buttonProps}
            clearDatesButton={clearDatesButton}
            onClearDates={onDatesClear}
            onNoEndDate={onNoEndDateHandler}
            onOverlayClose={onOverlayCloseHandler}
            showOverlayCloseButton={!autoClose}
            showNoEndDateButton={focused === FOCUSED_FIELD.TO && noEndDate}
            overlayCloseButton={overlayCloseButton}
            noEndDateButton={noEndDateLabel}
          />
        }
        OverlayInfo={
          <CalendarInfo
            AdditionalInfo={AdditionalInfo}
            minimumPeriodHint={minimumPeriodHint}
            moveInWindowsHint={moveInWindowsHint}
          />
        }
        to={to}
      >
        <Calendar
          {...datePickerProps}
          CaptionSubtitle={CaptionSubtitle}
          highlightMoveInWindows={focused === FOCUSED_FIELD.FROM}
          moveInWindows={moveInWindows}
          moveInWindowsTooltip={moveInWindowsTooltip}
          usePrevMonth={focused === null && autoClose}
        />
      </Overlay>
    </Grid>
  );
};

export default OverlayWrapper;
